/**
 * Circular Buffer Logger for Hybrid App Debugging
 * 
 * This module implements a persistent circular buffer optimized for crash debugging.
 * Uses an append-only delimited string format to ensure synchronous, non-blocking writes.
 * 
 * CRITICAL: This is a CRASH/DEBUG logger, not an analytics logger.
 * All writes must be synchronous to capture logs before potential crashes.
 * 
 * Features:
 * - Synchronous writes (no debouncing) for crash safety
 * - Append-only string format (no JSON parsing on write)
 * - O(1) string-based FIFO cleanup when limit is reached
 * - 300KB size limit for optimal performance
 * - Trace ID support for session tracking
 * - Monolog-compatible log structure
 */

const STORAGE_KEY = 'app_circular_buffer_logs';
const TRACE_ID_KEY = 'current_trace_id';
const LOG_DELIMITER = '\n'; // NDJSON format - newlines inside JSON are escaped as \\n
const MAX_SIZE_BYTES = 300 * 1024; // 300KB

export type LogLevel = 'info' | 'debug' | 'warning' | 'error';
export type LogLayer = 'web' | 'native';
export type LogCategory = 
  | 'session_start' 
  | 'deep_link' 
  | 'navigation' 
  | 'data_error' 
  | 'system' 
  | 'error'
  | 'user_action'
  | 'api_call'
  | 'performance'
  | 'crash';

export interface LogEntry {
  timestamp: string;
  level: LogLevel;
  layer: LogLayer;
  message: string;
  context: {
    category: LogCategory;
    trace_id?: string;
    [key: string]: any;
  };
}

/**
 * Get the current trace ID from URL parameters or generate a new one
 */
export function getOrGenerateTraceId(): string {
  // Check URL parameters for trace_id from native layer
  const urlParams = new URLSearchParams(globalThis.location.search);
  const urlTraceId = urlParams.get('trace_id');
  
  if (urlTraceId) {
    sessionStorage.setItem(TRACE_ID_KEY, urlTraceId);
    return urlTraceId;
  }
  
  // Check if we already have a trace ID in sessionStorage for this session
  const sessionTraceId = sessionStorage.getItem(TRACE_ID_KEY);
  if (sessionTraceId) {
    return sessionTraceId;
  }
  
  // Generate new trace ID
  const newTraceId = `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
  sessionStorage.setItem(TRACE_ID_KEY, newTraceId);
  
  return newTraceId;
}

/**
 * Get the raw log string from localStorage
 */
function getRawLogString(): string {
  try {
    return localStorage.getItem(STORAGE_KEY) || '';
  } catch (error) {
    console.error('Error reading from localStorage:', error);
    return '';
  }
}

/**
 * Save the raw log string to localStorage
 * Throws errors if save fails (including QuotaExceededError)
 */
function saveRawLogString(logString: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, logString);
  } catch (error) {
    // If quota exceeded, attempt emergency cleanup and retry
    if (error instanceof Error && error.name === 'QuotaExceededError') {
      const trimmed = emergencyTrim(logString);
      try {
        localStorage.setItem(STORAGE_KEY, trimmed);
        console.warn('Emergency trim applied due to quota exceeded');
      } catch (retryError) {
        console.error('Failed to save even after emergency trim:', retryError);
        throw retryError; // Throw so caller knows save failed
      }
    } else {
      console.error('Error saving to localStorage:', error);
      throw error; // Throw other errors too
    }
  }
}

/**
 * Emergency trim: Keep roughly the last 10KB to save CPU from parsing
 */
function emergencyTrim(logString: string): string {
  if (logString.length < 10000) {
    return logString;
  }
  return logString.substring(logString.length - 10000);
}

/**
 * Trim logs using string manipulation only (no JSON parsing)
 * Removes entries from the beginning until under size limit
 */
function trimLogStringToFit(logString: string): string {
  if (logString.length <= MAX_SIZE_BYTES) {
    return logString;
  }

  // Calculate how many bytes to remove (remove ~25% when over limit)
  const bytesToRemove = Math.floor(logString.length * 0.25);
  
  // Find the first delimiter after the bytes-to-remove point
  const firstDelimiterPos = logString.indexOf(LOG_DELIMITER, bytesToRemove);
  
  if (firstDelimiterPos === -1) {
    // If no delimiter found, keep everything (edge case)
    return logString;
  }
  
  // Return everything AFTER the newline (no need to add delimiter length, it's just \n)
  return logString.substring(firstDelimiterPos + 1);
}

/**
 * Add a log entry to the circular buffer
 * CRITICAL: This is synchronous and does NOT parse existing logs
 */
export function addLog(
  level: LogLevel,
  message: string,
  context: {
    category: LogCategory;
    [key: string]: any;
  }
): void {
  try {
    const logEntry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      layer: 'web',
      message,
      context: {
        ...context,
        trace_id: getOrGenerateTraceId()
      }
    };
    
    // Get existing log string
    let logString = getRawLogString();
    
    // Append new entry (no parsing!)
    const serializedEntry = JSON.stringify(logEntry);
    logString += serializedEntry + LOG_DELIMITER;
    
    // Trim if over limit (string manipulation only)
    logString = trimLogStringToFit(logString);
    
    // Save synchronously
    saveRawLogString(logString);
  } catch (error) {
    // Even error handling must not crash
    console.error('Critical error in addLog:', error);
  }
}

/**
 * Parse the raw log string into an array of LogEntry objects
 * ONLY used when reading logs (getAllLogs, mergeNativeLogs)
 */
function parseLogString(logString: string): LogEntry[] {
  if (!logString) {
    return [];
  }
  
  const entries = logString.split(LOG_DELIMITER).filter(Boolean);
  const parsed: LogEntry[] = [];
  
  for (const entry of entries) {
    try {
      const log = JSON.parse(entry) as LogEntry;
      parsed.push(log);
    } catch (error) {
      // Skip corrupted entries
      console.warn('Skipping corrupted log entry:', error);
    }
  }
  
  return parsed;
}

/**
 * Get all logs from the circular buffer
 * Automatically requests and merges native logs if in React Native context
 * This parses the log string - use sparingly
 */
export async function getAllLogs(): Promise<LogEntry[]> {
  // Request and merge native logs first (if in React Native)
  const nativeLogs = await requestNativeLogs();
  if (nativeLogs.length > 0) {
    try {
      // Attempt to merge and persist
      mergeNativeLogsSync(nativeLogs);
      
      // Only clear native logs if merge was successful
      clearNativeLogs();
    } catch (error) {
      console.error('Failed to merge native logs - native logs will be retained:', error);
      // Don't call clearNativeLogs() - let native layer keep the logs
      // They'll be attempted again on next getAllLogs() call
    }
  }
  
  const logString = getRawLogString();
  return parseLogString(logString);
}

/**
 * Get the current trace ID
 */
export function getCurrentTraceId(): string | null {
  return sessionStorage.getItem(TRACE_ID_KEY);
}

/**
 * Clear all logs from the circular buffer
 * Useful after successful submission of a support request
 */
export function clearLogs(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
    // Generate new trace ID for next session
    sessionStorage.removeItem(TRACE_ID_KEY);
    getOrGenerateTraceId();
  } catch (error) {
    console.error('Error clearing logs:', error);
  }
}

/**
 * Reset session: Clear logs and generate a new trace ID
 * Returns the new trace ID for immediate use
 * UI should call window.location.reload() after this
 */
export function resetSession(): string {
  try {
    localStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(TRACE_ID_KEY);
    const newTraceId = getOrGenerateTraceId();
    return newTraceId;
  } catch (error) {
    console.error('Error resetting session:', error);
    return getOrGenerateTraceId();
  }
}

/**
 * Get log statistics for displaying buffer usage
 */
export function getLogStats(): { sizeInBytes: number; entryCount: number } {
  try {
    const logString = getRawLogString();
    const sizeInBytes = new Blob([logString]).size;
    const entryCount = logString ? logString.split(LOG_DELIMITER).filter(line => line.trim()).length : 0;
    
    return { sizeInBytes, entryCount };
  } catch (error) {
    console.error('Error getting log stats:', error);
    return { sizeInBytes: 0, entryCount: 0 };
  }
}

/**
 * Get raw log export for copy/share feature
 * Returns the raw NDJSON string
 */
export function getRawLogExport(): string {
  return getRawLogString();
}

/**
 * Request logs from the native layer (React Native)
 * Returns a Promise that resolves with native logs or empty array if not in native context
 */
function requestNativeLogs(): Promise<LogEntry[]> {
  return new Promise((resolve) => {
    // Not in React Native context
    if (!(globalThis as any).ReactNativeWebView) {
      resolve([]);
      return;
    }
    
    // Set up one-time listener for response
    const handleResponse = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'NATIVE_LOGS_RESPONSE') {
          globalThis.removeEventListener('message', handleResponse);
          resolve(data.logs || []);
        }
      } catch (error) {
        console.error('Error parsing native logs response:', error);
      }
    };
    
    globalThis.addEventListener('message', handleResponse);
    
    // Send request to native layer
    try {
      (globalThis as any).ReactNativeWebView.postMessage(JSON.stringify({
        type: 'REQUEST_NATIVE_LOGS'
      }));
      
      // Timeout after 2 seconds
      setTimeout(() => {
        globalThis.removeEventListener('message', handleResponse);
        resolve([]);
      }, 2000);
    } catch (error) {
      console.error('Error requesting native logs:', error);
      globalThis.removeEventListener('message', handleResponse);
      resolve([]);
    }
  });
}

/**
 * Clear logs on the native layer after successful merge
 * Sends message to native layer to clear its circular buffer
 */
function clearNativeLogs(): void {
  try {
    if ((globalThis as any).ReactNativeWebView) {
      (globalThis as any).ReactNativeWebView.postMessage(JSON.stringify({
        type: 'CLEAR_NATIVE_LOGS'
      }));
    }
  } catch (error) {
    console.error('Error clearing native logs:', error);
  }
}

/**
 * Merge logs from native layer (received via bridge)
 * Internal function - use getAllLogs() instead which calls this automatically
 * Throws errors if merge fails so caller can decide whether to clear native logs
 */
function mergeNativeLogsSync(nativeLogs: LogEntry[]): void {
  // Parse existing logs (use direct read to avoid circular dependency)
  const logString = getRawLogString();
  const existingLogs = parseLogString(logString);
  
  // Merge arrays
  const allLogs = [...existingLogs, ...nativeLogs];
  
  // Sort by timestamp to maintain chronological order
  allLogs.sort((a, b) => 
    new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );
  
  // Re-serialize to delimited string format
  const mergedLogString = allLogs.map(log => JSON.stringify(log)).join(LOG_DELIMITER) + LOG_DELIMITER;
  
  // Trim if necessary
  const trimmedString = trimLogStringToFit(mergedLogString);
  
  // This will throw if it fails (e.g., QuotaExceededError)
  saveRawLogString(trimmedString);
}

/**
 * Initialize the circular buffer on app load
 * Logs the session start event
 */
export function initializeCircularBuffer(): void {
  // CRITICAL: Install crash handlers first
  installCrashHandlers();
  
  // Ensure trace ID is generated/retrieved
  getOrGenerateTraceId();
  
  // Log session start
  addLog('info', 'Web app loaded. Session initialized.', {
    category: 'session_start',
    entry_point: 'Web',
    url: globalThis.location.href,
    referrer: document.referrer || 'direct'
  });
}

/**
 * Install global error handlers for crash logging
 */
export function installCrashHandlers(): void {
  // Capture unhandled errors
  globalThis.addEventListener('error', (event) => {
    addLog('error', `Unhandled error: ${event.message}`, {
      category: 'crash',
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      error: event.error?.stack
    });
  });
  
  // Capture unhandled promise rejections
  globalThis.addEventListener('unhandledrejection', (event) => {
    addLog('error', `Unhandled rejection: ${event.reason}`, {
      category: 'crash',
      reason: String(event.reason),
      promise: 'Promise rejection'
    });
  });
}
